import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import KenyaLocationFields from '../../components/common/KenyaLocationFields';
import BusinessTypePicker from '../../components/common/BusinessTypePicker';
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  HomeIcon,
  MapPinIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const ACCOUNT_TYPES = [
  {
    id: 'farmer',
    name: 'Farmer',
    icon: HomeIcon,
    description: 'Manage your poultry farm, track records, and sell your products',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconColor: 'text-green-600'
  },
  {
    id: 'supplier',
    name: 'Supplier',
    icon: BuildingStorefrontIcon,
    description: 'Sell feed, equipment, medication, and supplies to farmers',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600'
  },
  {
    id: 'client',
    name: 'Customer',
    icon: ShoppingBagIcon,
    description: 'Browse and buy fresh eggs, chicken, and other products from farmers',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-600'
  }
];

const Register = () => {
  const { register, loading, error } = useAuth();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || null;
  
  const [step, setStep] = useState(initialType ? 2 : 1); // 1 = type selection, 2 = registration form
  const [accountType, setAccountType] = useState(initialType || 'farmer');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    county: '',
    subCounty: '',
    town: '',
    // Supplier-specific
    businessName: '',
    businessTypes: [],
    // Settlement settings for suppliers
    settlementMethod: 'mpesa',
    settlementPhone: '',
    settlementBankName: '',
    settlementBankAccount: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    if (name === 'phone') {
      if (!value) {
        setPhoneError('');
      } else {
        const p = parsePhoneNumberFromString(value, 'KE');
        setPhoneError(!p || !p.isValid() ? 'Enter a valid phone number' : '');
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Supplier-specific validation
    if (accountType === 'supplier') {
      if (!formData.businessName.trim()) errors.businessName = 'Business name is required';
      if (!formData.businessTypes.length) errors.businessType = 'Select at least one business type';
      // Settlement settings validation
      if (formData.settlementMethod === 'mpesa') {
        if (!formData.settlementPhone.trim()) errors.settlementPhone = 'M-Pesa phone number is required for receiving payments';
      } else if (formData.settlementMethod === 'bank') {
        if (!formData.settlementBankName.trim()) errors.settlementBankName = 'Bank name is required';
        if (!formData.settlementBankAccount.trim()) errors.settlementBankAccount = 'Bank account number is required';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // The field-level messages sit far above the button, so summarise here too.
      setFormErrors((prev) => ({ ...prev, submit: 'Please fix the highlighted fields above.' }));
      return;
    }

    const { confirmPassword, ...userData } = formData;
    userData.accountType = accountType;
    
    // Normalize phone
    if (userData.phone) {
      const p = parsePhoneNumberFromString(userData.phone, 'KE');
      if (!p || !p.isValid()) {
        setFormErrors((prev) => ({
          ...prev,
          phone: 'Enter a valid phone number',
          submit: 'Enter a valid phone number, or clear the field to skip it.'
        }));
        return;
      }
      userData.phone = p.number;
    }
    
    // Remove supplier fields if not supplier
    if (accountType !== 'supplier') {
      delete userData.businessName;
      delete userData.businessType;
      delete userData.businessTypes;
      delete userData.settlementMethod;
      delete userData.settlementPhone;
      delete userData.settlementBankName;
      delete userData.settlementBankAccount;
    } else {
      userData.businessType = formData.businessTypes[0];
      userData.businessTypes = formData.businessTypes;
    }
    
    try {
      const res = await authAPI.register(userData);
      if (res.status === 202) {
        // OTP verification required
        setPendingEmail(userData.email);
        setOtpMode(true);
        return;
      }
      // Immediate registration success (legacy path)
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        // Route based on account type
        const redirectPath = accountType === 'supplier' ? '/supplier/dashboard' : 
                            accountType === 'client' ? '/client/dashboard' : '/dashboard';
        window.location.replace(redirectPath);
      }
    } catch (err) {
      // Show error to user instead of retrying
      const errorMsg = err.response?.data?.message || err.response?.data?.details || 'Registration failed';
      setFormErrors({ ...formErrors, submit: errorMsg });
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!otp || !pendingEmail) return;
    const res = await authAPI.verifyRegistration(pendingEmail, otp);
    if (res?.data?.token) {
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // Route based on account type
      const redirectPath = accountType === 'supplier' ? '/supplier/dashboard' : 
                          accountType === 'client' ? '/client/dashboard' : '/dashboard';
      window.location.replace(redirectPath);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    await authAPI.resendVerification(pendingEmail);
    alert('Verification code resent. Please check your email.');
  };

  const selectAccountType = (type) => {
    setAccountType(type);
    setStep(2);
  };

  // Step 1: Account Type Selection
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">🐔</span>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">Join Omabra</h2>
            <p className="mt-2 text-gray-600">Choose how you want to use Omabra</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {ACCOUNT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => selectAccountType(type.id)}
                className={`${type.bgColor} ${type.borderColor} border-2 rounded-2xl p-6 text-left hover:shadow-lg transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${type.color} flex items-center justify-center mb-4`}>
                  <type.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.name}</h3>
                <p className="text-sm text-gray-600">{type.description}</p>
              </button>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration Form
  const selectedType = ACCOUNT_TYPES.find(t => t.id === accountType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => setStep(1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Change account type
          </button>
          <div className={`mx-auto h-16 w-16 bg-gradient-to-r ${selectedType?.color || 'from-blue-600 to-green-600'} rounded-2xl flex items-center justify-center shadow-lg`}>
            {selectedType?.icon ? <selectedType.icon className="h-8 w-8 text-white" /> : <span className="text-white font-bold text-2xl">🐔</span>}
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {accountType === 'farmer' && 'Join as Farmer'}
            {accountType === 'supplier' && 'Join as Supplier'}
            {accountType === 'client' && 'Join as Customer'}
          </h2>
          <p className="mt-2 text-gray-600">{selectedType?.description}</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="mb-6">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        
          {!otpMode ? (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Supplier: Business Info */}
            {accountType === 'supplier' && (
              <div className="space-y-4 pb-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Business Information</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      name="businessName"
                      type="text"
                      required
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.businessName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                      placeholder="Your business name"
                      value={formData.businessName}
                      onChange={handleChange}
                    />
                  </div>
                  {formErrors.businessName && <p className="mt-1 text-sm text-red-600">{formErrors.businessName}</p>}
                </div>
                <div>
                  <BusinessTypePicker
                    value={formData.businessTypes}
                    onChange={(businessTypes) => {
                      setFormData((prev) => ({ ...prev, businessTypes }));
                      if (formErrors.businessType) setFormErrors((prev) => ({ ...prev, businessType: '' }));
                    }}
                    error={formErrors.businessType}
                  />
                </div>
                
                {/* Settlement Settings */}
                <div className="pt-3 mt-3 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Payment Settings *</h4>
                  <p className="text-sm text-gray-500 mb-3">Where should we send your earnings when you request a withdrawal?</p>
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <select
                      name="settlementMethod"
                      required
                      className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.settlementMethod}
                      onChange={handleChange}
                    >
                      <option value="mpesa">M-Pesa</option>
                      <option value="bank">Bank Transfer</option>
                    </select>
                  </div>
                  
                  {formData.settlementMethod === 'mpesa' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">M-Pesa Phone Number *</label>
                      <input
                        name="settlementPhone"
                        type="tel"
                        required
                        className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.settlementPhone ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        placeholder="07XXXXXXXX"
                        value={formData.settlementPhone}
                        onChange={handleChange}
                      />
                      {formErrors.settlementPhone && <p className="mt-1 text-sm text-red-600">{formErrors.settlementPhone}</p>}
                    </div>
                  )}
                  
                  {formData.settlementMethod === 'bank' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                        <input
                          name="settlementBankName"
                          type="text"
                          required
                          className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.settlementBankName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          placeholder="e.g. Equity Bank"
                          value={formData.settlementBankName}
                          onChange={handleChange}
                        />
                        {formErrors.settlementBankName && <p className="mt-1 text-sm text-red-600">{formErrors.settlementBankName}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                        <input
                          name="settlementBankAccount"
                          type="text"
                          required
                          className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.settlementBankAccount ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          placeholder="Your bank account number"
                          value={formData.settlementBankAccount}
                          onChange={handleChange}
                        />
                        {formErrors.settlementBankAccount && <p className="mt-1 text-sm text-red-600">{formErrors.settlementBankAccount}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="firstName"
                    type="text"
                    required
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                {formErrors.firstName && <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="lastName"
                    type="text"
                    required
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
                {formErrors.lastName && <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-gray-400">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="phone"
                  type="tel"
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${phoneError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPinIcon className="h-4 w-4 inline mr-1" />
                Location <span className="text-gray-400">(Optional - helps with nearby discovery)</span>
              </label>
              <KenyaLocationFields
                county={formData.county}
                subCounty={formData.subCounty}
                town={formData.town}
                onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
              
              {formData.password && (
                <div className="mt-2">
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-2 flex-1 rounded-full ${
                          level <= passwordStrength
                            ? passwordStrength <= 2 ? 'bg-red-400' : passwordStrength <= 3 ? 'bg-yellow-400' : 'bg-green-400'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {formErrors.password && <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
              {formErrors.confirmPassword && <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>}
            </div>

            {(error || formErrors.submit) && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <div className="flex items-center">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                  <div className="text-sm text-red-700">{formErrors.submit || error}</div>
                </div>
              </div>
            )}

            <div className="flex items-start">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
              />
              <label htmlFor="terms" className="ml-3 block text-sm text-gray-700">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 hover:text-blue-500 font-medium">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-500 font-medium">Privacy Policy</Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r ${selectedType?.color || 'from-blue-600 to-green-600'} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Creating Account...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Create {accountType === 'farmer' ? 'Farmer' : accountType === 'supplier' ? 'Supplier' : 'Customer'} Account
                </>
              )}
            </button>
          </form>
          ) : (
            <form className="space-y-5" onSubmit={handleVerify}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enter verification code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  placeholder="6-digit code"
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  We sent a code to {pendingEmail}.{' '}
                  <button type="button" onClick={handleResend} className="text-blue-600 hover:underline">Resend code</button>
                </p>
              </div>
              <button type="submit" className={`w-full flex justify-center items-center py-3 px-4 rounded-lg text-white bg-gradient-to-r ${selectedType?.color || 'from-blue-600 to-green-600'}`}>
                Verify & Create Account
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
